import React, { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon, MapPinIcon } from "lucide-react";
import { FaEnvelope } from "react-icons/fa";
import PageLayout from "../../../components/Layout/PageLayout";
import Hyperlink from "../../../components/elements/HyperLink";

// Types
interface AccordionItemProps {
  title: string;
  children: React.ReactNode;
  isOpen?: boolean;
  onToggle: () => void;
}

interface RegionalAnnexProps {
  region: string;
  laws: string;
  content: React.ReactNode;
}

// Accordion Component
const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  children,
  isOpen,
  onToggle,
}) => {
  return (
    <div className="border-b border-primary/10">
      <button
        className="flex justify-between items-center w-full md:py-6 py-3 px-3 md:px-5 text-left hover:bg-gray-50 transition-colors duration-200"
        onClick={onToggle}
      >
        <h3 className="text-lg font-medium text-gray-900 pr-4">{title}</h3>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-5 px-3 md:px-5 prose prose-gray max-w-none">
          {children}
        </div>
      )}
    </div>
  );
};

// Regional Annex Component
const RegionalAnnex: React.FC<RegionalAnnexProps> = ({
  region,
  laws,
  content,
}) => {
  return (
    <div className="bg-[white]/5 rounded-lg p-4 mb-4">
      <div className="flex items-center mb-4">
        <div className="bg-blue-100 rounded-full p-2 mr-3">
          <MapPinIcon className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h4 className="text-lg font-medium text-gray-900">{region}</h4>
          <p className="text-sm text-blue-600">{laws}</p>
        </div>
      </div>
      <div className="text-gray-700">{content}</div>
    </div>
  );
};

const PrivacyPolicy: React.FC = () => {
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  const toggleSection = (index: number) => {
    const newOpenSections = new Set(openSections);
    if (newOpenSections.has(index)) {
      newOpenSections.delete(index);
    } else {
      newOpenSections.add(index);
    }
    setOpenSections(newOpenSections);
  };

  const now = new Date();
  const options = { 
    year: 'numeric', 
    month: 'long', 
    // day: 'numeric' 
  } as const; 

  const formattedDate = now.toLocaleDateString('en-US', options);

  const sections = [
    {
      title: "Scope & Roles",
      content: (
        <div className="space-y-4">
          <p>We provide an AI‑enabled recruitment platform.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg border border-primary/10">
              <h4 className="font-medium text-gray-900 mb-2">
                Controller Context
              </h4>
              <p className="text-sm text-gray-600">
                We act as a controller for our own website/app operations,
                analytics, support, security, and product improvement.
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-primary/10">
              <h4 className="font-medium text-gray-900 mb-2">
                Processor Context
              </h4>
              <p className="text-sm text-gray-600">
                For candidate data processed on behalf of a customer, we act
                only on the customer's documented instructions. A Data
                Processing Addendum (DPA) applies.
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Personal Data We Collect",
      content: (
        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">You Provide</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
              <li>
                Name, contact details, professional profile (CV/resumé,
                education, skills, portfolios)
              </li>
              <li>Application preferences (roles, locations, compensation)</li>
              <li>
                Interview artifacts (assessments, recordings/transcripts if
                enabled)
              </li>
              <li>Communications, and consent/choice settings</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">From Others</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
              <li>Referees/prior employers (where lawful)</li>
              <li>
                Job boards, talent networks, publicly available professional
                profiles
              </li>
              <li>Background‑check providers (customer option)</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Automatically</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
              <li>Device and usage data, approximate location, cookies/SDKs</li>
              <li>
                Session telemetry, and model outputs (parsed attributes, scores,
                rankings, embeddings)
              </li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-[grey]/20 rounded-lg p-4">
            <h4 className="font-medium text-yellow-800 mb-2">Sensitive Data</h4>
            <p className="text-yellow-700 text-sm">
              We do not seek special‑category data. If a role requires checks
              (e.g., right‑to‑work, sanctions/criminal where lawful), we process
              only as permitted and as instructed.
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Purposes & Legal Bases",
      content: (
        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Purposes</h4>
            <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
              <li>
                Provide, operate, and secure the Service (authentication,
                fraud/abuse detection, service monitoring)
              </li>
              <li>
                Recruit and match candidates (parsing, skills extraction,
                recommendations, scheduling)
              </li>
              <li>
                Facilitate evaluation (structured interviews, assessments,
                feedback)
              </li>
              <li>
                Support and troubleshooting; improve and develop features and
                model quality
              </li>
              <li>Customer account administration, billing, and reporting</li>
              <li>Compliance with law and valid legal requests</li>
              <li>
                Our own marketing/communications in controller context only
                (with appropriate choices/opt‑outs)
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Legal Bases</h4>
            <p className="text-gray-600">
              We rely on one or more of: performance of (or steps to enter into)
              a contract, our legitimate interests, consent (where required),
              and/or compliance with legal obligations. Local definitions vary,
              see Regional Annexes.
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Automated Decision‑Making & Profiling",
      content: (
        <div className="space-y-4">
          <p>
            The Service may parse, score, and rank applications against job
            requirements using machine learning.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg border border-primary/10">
              <h4 className="font-medium text-gray-900 mb-2">Inputs</h4>
              <p className="text-sm text-gray-600">
                Text and structured data (e.g., skills, experience) and
                employer‑defined criteria.
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-primary/10">
              <h4 className="font-medium text-gray-900 mb-2">Exclusions</h4>
              <p className="text-sm text-gray-600">
                Systems are designed to exclude protected characteristics from
                evaluation.
              </p>
            </div>
          </div>

          <div className="bg-green-50 border border-[grey]/20 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">Your Rights</h4>
            <p className="text-green-700 text-sm">
              Customers can require human review before adverse outcomes. You
              may request human review, share your perspective, or contest a
              decision, where applicable.
            </p>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">
              Fairness Controls
            </h4>
            <p className="text-gray-600 text-sm">
              Bias testing, explainability summaries, and monitoring features
              are available to customers.
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Sharing & Recipients",
      content: (
        <div className="space-y-4">
          <p>We share personal data with:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
            <li>Customers (employers) and their authorized users</li>
            <li>
              Service providers/subprocessors bound by confidentiality,
              security, and data‑protection obligations
            </li>
            <li>Professional advisors and authorities as required</li>
            <li>
              Parties to a corporate transaction under equivalent protections
            </li>
          </ul>
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-blue-700 text-sm">
              <b>Subprocessors list:</b>{" "}
              <a
                href="https://25thandstaffing.com/subprocessors"
                className="underline hover:text-blue-800"
                target="_blank"
                rel="noopener noreferrer"
              >
                25thandstaffing.com/subprocessors
              </a>
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "International Data Transfers",
      content: (
        <div className="space-y-4">
          <p>
            We may transfer data where we or our providers operate. We apply
            appropriate contractual, technical, and organizational safeguards.
            Where specific transfer tools are required (e.g., SCCs, IDTA), these
            are addressed in the Regional Annexes or the DPA.
          </p>
        </div>
      ),
    },
    {
      title: "Retention",
      content: (
        <div className="space-y-6">
          <p>
            We keep personal data only as long as needed for the purposes above
            or as required by law/contract.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg border border-primary/10">
              <h4 className="font-medium text-gray-900 mb-2">
                Applicants not hired
              </h4>
              <p className="text-sm text-gray-600">
                12–24 months from last activity
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-primary/10">
              <h4 className="font-medium text-gray-900 mb-2">Talent pool</h4>
              <p className="text-sm text-gray-600">
                Until withdrawal or 24 months of inactivity
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-primary/10">
              <h4 className="font-medium text-gray-900 mb-2">
                Security/diagnostic logs
              </h4>
              <p className="text-sm text-gray-600">12–18 months</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-primary/10">
              <h4 className="font-medium text-gray-900 mb-2">
                Account & compliance
              </h4>
              <p className="text-sm text-gray-600">As legally required</p>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            At the end of retention, we delete or anonymize data. Customers may
            set retention in admin settings.
          </p>
        </div>
      ),
    },
    {
      title: "Security",
      content: (
        <div className="space-y-4">
          <p>We implement industry‑standard safeguards including:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
            <li>Encryption in transit/at rest</li>
            <li>Access controls and monitoring</li>
            <li>Secure SDLC and vulnerability management</li>
            <li>Employee training</li>
          </ul>
          <p className="text-gray-600">
            We maintain an incident response program and will notify of
            qualifying data breaches per applicable laws and contracts.
          </p>
        </div>
      ),
    },
    {
      title: "Your Rights & Choices",
      content: (
        <div className="space-y-6">
          <p>
            Depending on your location, you may have rights to access, correct,
            delete, port, or restrict/object to certain processing; withdraw
            consent (where applicable); and opt‑out of certain uses.
          </p>

          <div className="bg-green-50 border border-[grey]/20 rounded-lg p-4">
            <div className="flex items-start">
              <FaEnvelope className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-green-800 mb-1">
                  How to Exercise Your Rights
                </h4>
                <p className="text-green-700 text-sm mb-2">
                  Use the in‑product Privacy Center or contact:{" "}
                  <Hyperlink
                    href="mailto:privacy@25thmail.com"
                    className="text-primary"
                  >
                    privacy@25thmail.com
                  </Hyperlink>
                </p>
                <p className="text-green-700 text-sm">
                  We will verify and respond within required timeframes. You may
                  use an authorized agent where permitted.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">
              Appeals & Complaints
            </h4>
            <p className="text-gray-600 text-sm">
              Appeal outcomes at{" "}
              <Hyperlink
                href="mailto:privacy@25thmail.com"
                className="text-primary"
              >
                privacy@25thmail.com
              </Hyperlink>{" "}
              and/or contact your local data protection authority (see Regional
              Annexes).
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Children",
      content: (
        <div className="space-y-4">
          <p>
            The Service is intended for applicants 18+. We do not knowingly
            collect data from children.
          </p>
          <div className="bg-yellow-50 border border-[grey]/20 rounded-lg p-4">
            <p className="text-yellow-700 text-sm">
              If you believe a child provided data, contact us to remove it at{" "}
              <Hyperlink
                href="mailto:privacy@25thmail.com"
                className="text-primary"
              >
                privacy@25thmail.com
              </Hyperlink>
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Cookies & Similar Technologies",
      content: (
        <div className="space-y-4">
          <p>
            We use cookies/SDKs for core functionality and analytics and in
            controller context for communications/marketing. Where required, we
            display a consent manager and honor your choices.
          </p>
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-blue-700 text-sm">
              See our{" "}
              <a href="#" className="underline hover:text-blue-800">
                Cookie Notice
              </a>{" "}
              for more information.
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Processor Commitments",
      content: (
        <div className="space-y-4">
          <p>When acting on customer instructions, we will:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
            <li>Process data only on documented instructions</li>
            <li>Ensure confidentiality and implement appropriate security</li>
            <li>
              Assist with data subject requests, impact assessments, and breach
              notifications
            </li>
            <li>Return/delete data at end of services</li>
            <li>
              Maintain and disclose our subprocessor list and notify of material
              changes
            </li>
            <li>
              Make audit/assessment information available as set out in the DPA
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "Contact",
      content: (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg pb-6 px-6">
            <h4 className="font-medium text-gray-900 mb-4">
              Provider/Controller
            </h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                <span className="text-gray-700">25th and Staffing</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                <span className="text-gray-700">
                  Architect Place, 2, Idowu Taylor Street, Victoria Island,
                  Lagos
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                <Hyperlink
                  href="mailto:privacy@25thmail.com"
                  className="text-primary"
                >
                  privacy@25thmail.com
                </Hyperlink>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                <Hyperlink
                  href="mailto:solutions@25thmail.com"
                  className="text-primary"
                >
                  solutions@25thmail.com (General Inquiries)
                </Hyperlink>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Changes",
      content: (
        <div className="space-y-4">
          <p>
            We may update this Policy from time to time. Material changes will
            be communicated via the Service or email. Please review the "Last
            updated" date above.
          </p>
        </div>
      ),
    },
  ];

  return (
    <PageLayout>
      <div>
        <div className="bg-white rounded-[10px] shadow-sm mt-3 mb-5">
          <div className="text-center">
            <h1 className="text-[20px] md:text-[35px] text-gray-900 mb-2">
              Privacy Policy
            </h1>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm text-gray-600 mb-4">
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                Last updated: {formattedDate}
              </div>
              <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">
                Applies to: All platform users
              </div>
            </div>
          </div>

          <div className="border-t border-primary/10 pt-6">
            <h2 className="text-lg font-medium text-gray-900 mb-3">
              Who We Are
            </h2>
            <div className="bg-gray-50 rounded-lg py-2">
              <p className="text-gray-700">
                <b>25th and Staffing</b>
                <br />
                2 Idowu Taylor Street, Victoria Island, Lagos, Nigeria
                <br />
                ("we", "us", "our")
                <br />
                Contact:{" "}
                <Hyperlink
                  href="mailto:privacy@25thmail.com"
                  className="text-primary"
                >
                  privacy@25thmail.com
                </Hyperlink>
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-[10px] shadow-sm border border-primary/10 overflow-hidden">
          {sections.map((section, index) => (
            <AccordionItem
              key={index}
              title={section.title}
              isOpen={openSections.has(index)}
              onToggle={() => toggleSection(index)}
            >
              {section.content}
            </AccordionItem>
          ))}
        </div>

        {/* Regional Annexes */}
        <div className="bg-white rounded-[10px] shadow-sm border border-primary/10 p-5 mt-5">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Regional Annexes
          </h2>

          <RegionalAnnex
            region="United States"
            laws="CPRA / other state laws"
            content={
              <div className="space-y-4">
                <p>
                  <b>Scope:</b> Applies to US residents in states with consumer
                  privacy laws.
                </p>

                <div className="bg-white border border-primary/10 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    Consumer Rights
                  </h5>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm ml-4">
                    <li>Right to know/access, delete, correct, portability</li>
                    <li>Opt‑out of sale or sharing of personal information</li>
                    <li>
                      Opt‑out of targeted advertising/profiling where applicable
                    </li>
                    <li>
                      Limit use/disclosure of sensitive personal information
                      (SPI)
                    </li>
                    <li>Non‑discrimination for exercising rights</li>
                  </ul>
                </div>

                <div className="bg-green-50 border border-[grey]/20 rounded-lg p-4">
                  <h5 className="font-medium text-green-800 mb-2">
                    Important Notes
                  </h5>
                  <ul className="space-y-2 text-green-700 text-sm">
                    <li>
                      • We do not currently "sell" or "share" personal
                      information as defined by the CPRA
                    </li>
                    <li>
                      • We honor Global Privacy Control (GPC) where required
                    </li>
                    <li>• Use Privacy Center or email to exercise rights</li>
                    <li>
                      • Appeals:{" "}
                      <Hyperlink
                        href="mailto:privacy@25thmail.com"
                        className=""
                      >
                        privacy@25thmail.com
                      </Hyperlink>
                    </li>
                  </ul>
                </div>
              </div>
            }
          />

          <RegionalAnnex
            region="Nigeria"
            laws="NDPA 2023 / NDPD"
            content={
              <div className="space-y-4">
                <p>
                  <b>Scope:</b> Applies to processing in the context of Nigeria
                  and to Nigerian data subjects.
                </p>

                <div className="bg-white border border-primary/10 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    Data Subject Rights
                  </h5>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm ml-4">
                    <li>Access, correction, deletion, portability</li>
                    <li>Restriction, objection, withdrawal of consent</li>
                    <li>Right to lodge complaints with NDPC</li>
                  </ul>
                </div>

                <div className="bg-blue-50 border border-[grey]/20 rounded-lg p-4">
                  <h5 className="font-medium text-blue-800 mb-2">
                    Governance & Compliance
                  </h5>
                  <ul className="space-y-2 text-blue-700 text-sm">
                    <li>
                      • Appropriate technical and organizational measures
                      implemented
                    </li>
                    <li>
                      • Cross‑border transfers allowed based on adequacy or
                      appropriate safeguards
                    </li>
                    <li>• DPO appointed where required</li>
                    <li>• May engage licensed DPCO for audits/compliance</li>
                  </ul>
                </div>

                <p className="text-sm text-gray-600">
                  <b>NDPC:</b>{" "}
                  <a
                    href="https://ndpc.gov.ng"
                    className="underline hover:text-gray-800"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://ndpc.gov.ng
                  </a>{" "}
                  for guidance and complaints.
                </p>
              </div>
            }
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default PrivacyPolicy;
